import React, { useState, useEffect, useMemo } from 'react';
import api, { inventoryAPI, itemsAPI, warehousesAPI } from '@/lib/api';
import { StockJustoExportDialog } from './StockJustoExportDialog';
import { Package, Clock, User, Filter, RefreshCw, ArrowUpCircle, ArrowDownCircle, Settings, AlertTriangle, Boxes, Search, Download, Warehouse, Copy, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';

const AdminInventoryPage = () => {
  const [movements, setMovements] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedItem, setSelectedItem] = useState('all');
  const [limit, setLimit] = useState(50);
  const [activeTab, setActiveTab] = useState('current');
  const [showInBoxes, setShowInBoxes] = useState(false);
  
  // Filtros para inventario actual
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [warehouseFilter, setWarehouseFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [justoOrigenIds, setJustoOrigenIds] = useState([]);
  const [stockJustoOpen, setStockJustoOpen] = useState(false);

  useEffect(() => {
    api.get('/justo/config').then((r) => setJustoOrigenIds(r.data.origen_warehouse_ids || [])).catch(() => {});
  }, []);

  // Obtener proveedores únicos de los items
  const suppliers = useMemo(() => {
    const uniqueSuppliers = [...new Set(items.map(item => item.supplier).filter(Boolean))];
    return uniqueSuppliers.sort();
  }, [items]);

  // Filtrar items para inventario actual - por bodega Y por proveedor
  const filteredInventory = useMemo(() => {
    return items
      .map(item => {
        // Determinar el stock a mostrar según el filtro de bodega
        let stockToShow = 0;
        const stockByWh = item.stock_by_warehouse || {};
        
        if (warehouseFilter && warehouseFilter !== 'all') {
          // Si hay filtro de bodega, mostrar el stock específico de esa bodega
          stockToShow = stockByWh[warehouseFilter] || 0;
        } else {
          // "Todas las bodegas": mostrar suma de stock_by_warehouse
          // Si stock_by_warehouse existe y tiene valores, usar la suma
          // Si no, usar current_stock como fallback
          const sumByWh = Object.values(stockByWh).reduce((acc, val) => acc + (val || 0), 0);
          stockToShow = sumByWh > 0 ? sumByWh : (item.current_stock || 0);
        }
        
        return {
          ...item,
          display_stock: stockToShow
        };
      })
      .filter(item => {
        // Filtrar por bodega: mostrar items que tengan stock en la bodega seleccionada
        let matchesWarehouse = true;
        if (warehouseFilter && warehouseFilter !== 'all') {
          // Mostrar si el item pertenece a esta bodega O tiene stock en esta bodega
          const hasStockInWarehouse = item.stock_by_warehouse && 
            item.stock_by_warehouse[warehouseFilter] !== undefined;
          const belongsToWarehouse = item.warehouse_id === warehouseFilter;
          matchesWarehouse = hasStockInWarehouse || belongsToWarehouse;
        }
        
        const matchesSupplier = supplierFilter === 'all' || item.supplier === supplierFilter;
        const matchesSearch = !searchTerm || 
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.code.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesWarehouse && matchesSupplier && matchesSearch;
      });
  }, [items, supplierFilter, searchTerm, warehouseFilter]);

  // Calcular totales por proveedor
  const inventoryStats = useMemo(() => {
    const stats = {
      totalProducts: filteredInventory.length,
      totalStock: filteredInventory.reduce((sum, item) => sum + (item.display_stock || 0), 0),
      lowStock: filteredInventory.filter(item => (item.display_stock || 0) <= (item.minimum_stock || 5) && (item.display_stock || 0) > 0).length,
      outOfStock: filteredInventory.filter(item => (item.display_stock || 0) === 0).length,
    };
    return stats;
  }, [filteredInventory]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadMovements();
  }, [selectedItem, limit]);

  const loadData = async () => {
    try {
      const [movementsRes, itemsRes, warehousesRes] = await Promise.all([
        inventoryAPI.getMovements(null, limit),
        itemsAPI.getAll(false),
        warehousesAPI.getActive()
      ]);
      setMovements(movementsRes.data);
      setItems(itemsRes.data);
      setWarehouses(warehousesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const loadMovements = async () => {
    try {
      const itemId = selectedItem === 'all' ? null : selectedItem;
      const response = await inventoryAPI.getMovements(itemId, limit);
      setMovements(response.data);
    } catch (error) {
      toast.error('Error al cargar movimientos');
    }
  };

  const syncStock = async () => {
    setSyncing(true);
    try {
      // Usar recalculate-totals que calcula current_stock desde stock_by_warehouse
      const response = await inventoryAPI.recalculateTotals();
      toast.success(response.data.message);
      // Recargar datos después de sincronizar
      await loadData();
    } catch (error) {
      toast.error('Error al sincronizar stock');
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      return format(date, "dd MMM yyyy, HH:mm", { locale: es });
    } catch {
      return dateStr;
    }
  };

  const getMovementTypeInfo = (type) => {
    switch (type) {
      case 'add':
        return { label: 'Entrada', icon: ArrowUpCircle, color: 'text-green-600 bg-green-100' };
      case 'subtract':
        return { label: 'Salida', icon: ArrowDownCircle, color: 'text-red-600 bg-red-100' };
      case 'set':
        return { label: 'Ajuste', icon: Settings, color: 'text-blue-600 bg-blue-100' };
      case 'reception':
        return { label: 'Recepción', icon: ArrowUpCircle, color: 'text-green-600 bg-green-100' };
      default:
        return { label: type, icon: Package, color: 'text-slate-600 bg-slate-100' };
    }
  };

  const getReasonBadgeColor = (reason) => {
    switch (reason) {
      case 'Conteo físico':
        return 'bg-purple-100 text-purple-700';
      case 'Recepción de proveedor':
        return 'bg-green-100 text-green-700';
      case 'Despacho a locales':
        return 'bg-tupu-orange-100 text-tupu-orange-700';
      case 'Merma':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  // Exportar inventario a CSV
  const exportInventoryCSV = () => {
    const selectedWarehouse = warehouses.find(w => w.id === warehouseFilter);
    const warehouseName = selectedWarehouse ? selectedWarehouse.name : 'todas';
    
    const headers = ['Código', 'Producto', 'Proveedor', 'Stock', 'Stock Mínimo', 'Unidad'];
    const rows = filteredInventory.map(item => [
      item.code,
      item.name,
      item.supplier || 'Sin proveedor',
      item.display_stock ?? item.current_stock ?? 0,
      item.minimum_stock || 0,
      item.unit || 'unidad'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inventario_${warehouseName}_${supplierFilter === 'all' ? 'todos' : supplierFilter}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Inventario exportado');
  };

  // Generar inventario completo de la bodega para WhatsApp (ignora filtros de proveedor/búsqueda)
  const getFullWarehouseInventory = () => {
    if (!warehouseFilter || warehouseFilter === 'all') return [];
    
    return items
      .map(item => {
        const stockByWh = item.stock_by_warehouse || {};
        const stockInWarehouse = stockByWh[warehouseFilter];
        // Solo incluir si tiene stock definido en esta bodega (incluyendo 0)
        if (stockInWarehouse === undefined) return null;
        return {
          name: item.name,
          stock: stockInWarehouse,
          supplier: item.supplier || null
        };
      })
      .filter(item => item !== null);
  };

  // Generar texto formateado para WhatsApp
  const generateWhatsAppText = () => {
    const inventoryItems = getFullWarehouseInventory();
    if (inventoryItems.length === 0) return null;

    const selectedWarehouse = warehouses.find(w => w.id === warehouseFilter);
    const warehouseName = selectedWarehouse?.name || 'Bodega';

    // Obtener fecha y hora actual en zona horaria de Chile
    const now = new Date();
    const chileDate = now.toLocaleDateString('es-CL', { 
      timeZone: 'America/Santiago',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const chileTime = now.toLocaleTimeString('es-CL', { 
      timeZone: 'America/Santiago',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    // Agrupar productos por proveedor
    const groupedBySupplier = {};
    const noSupplier = [];

    inventoryItems.forEach(item => {
      if (item.supplier) {
        if (!groupedBySupplier[item.supplier]) {
          groupedBySupplier[item.supplier] = [];
        }
        groupedBySupplier[item.supplier].push(item);
      } else {
        noSupplier.push(item);
      }
    });

    // Ordenar proveedores alfabéticamente
    const sortedSuppliers = Object.keys(groupedBySupplier).sort((a, b) => 
      a.localeCompare(b, 'es', { sensitivity: 'base' })
    );

    // Ordenar productos dentro de cada proveedor alfabéticamente
    sortedSuppliers.forEach(supplier => {
      groupedBySupplier[supplier].sort((a, b) => 
        (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
      );
    });

    // Ordenar productos sin proveedor alfabéticamente
    noSupplier.sort((a, b) => 
      (a.name || '').localeCompare(b.name || '', 'es', { sensitivity: 'base' })
    );

    // Construir el texto
    let text = `INVENTARIO ACTUAL — ${warehouseName.toUpperCase()}\n`;
    text += `${chileDate} - ${chileTime} hrs\n`;

    // Agregar productos agrupados por proveedor
    sortedSuppliers.forEach(supplier => {
      text += `\n*${supplier.toUpperCase()}*\n`;
      groupedBySupplier[supplier].forEach(item => {
        const qty = Math.floor(item.stock);
        text += `${qty} ${item.name}\n`;
      });
    });

    // Agregar productos sin proveedor al final
    if (noSupplier.length > 0) {
      text += `\n*SIN PROVEEDOR*\n`;
      noSupplier.forEach(item => {
        const qty = Math.floor(item.stock);
        text += `${qty} ${item.name}\n`;
      });
    }

    return text.trim();
  };

  // Copiar inventario completo de la bodega al portapapeles para WhatsApp
  const copyInventoryToWhatsApp = async () => {
    if (!warehouseFilter || warehouseFilter === 'all') {
      toast.error('Selecciona una bodega específica para copiar su inventario.');
      return;
    }

    const inventoryItems = getFullWarehouseInventory();
    if (inventoryItems.length === 0) {
      toast.error('No hay productos disponibles para copiar.');
      return;
    }

    const text = generateWhatsAppText();
    if (!text) {
      toast.error('No hay productos disponibles para copiar.');
      return;
    }

    const selectedWarehouse = warehouses.find(w => w.id === warehouseFilter);
    const warehouseName = selectedWarehouse?.name || 'Bodega';

    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Inventario de ${warehouseName} copiado para WhatsApp`);
    } catch (error) {
      console.error('Error copiando al portapapeles:', error);
      toast.error('Error al copiar. Intenta de nuevo.');
    }
  };

  // Verificar si el botón de WhatsApp debe estar habilitado
  const isWhatsAppEnabled = warehouseFilter && warehouseFilter !== 'all';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-inventory-page">
      <StockJustoExportDialog warehouseId={warehouseFilter} open={stockJustoOpen} onOpenChange={setStockJustoOpen} />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Inventario</h1>
          <p className="text-muted-foreground">Gestión de stock y registro de movimientos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={syncStock} disabled={syncing}>
            <AlertTriangle className={`w-4 h-4 mr-2 ${syncing ? 'animate-pulse' : ''}`} />
            {syncing ? 'Recalculando...' : 'Recalcular Stock'}
          </Button>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="current" className="flex items-center gap-2">
            <Boxes className="w-4 h-4" />
            Inventario Actual
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Historial de Movimientos
          </TabsTrigger>
        </TabsList>

        {/* Tab: Inventario Actual */}
        <TabsContent value="current" className="space-y-6">
          {/* Filtros */}
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Bodega</label>
                <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
                  <SelectTrigger data-testid="warehouse-filter">
                    <Warehouse className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Todas las bodegas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las bodegas (global)</SelectItem>
                    {warehouses.map(wh => (
                      <SelectItem key={wh.id} value={wh.id}>
                        {wh.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Proveedor</label>
                <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                  <SelectTrigger data-testid="supplier-filter">
                    <Filter className="w-4 h-4 mr-2 text-slate-400" />
                    <SelectValue placeholder="Todos los proveedores" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los proveedores</SelectItem>
                    {suppliers.map(supplier => (
                      <SelectItem key={supplier} value={supplier}>
                        {supplier}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-slate-700 mb-1 block">Buscar producto</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre o código..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="inventory-search"
                  />
                </div>
              </div>
              <div className="flex items-end gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button 
                          variant="outline" 
                          onClick={copyInventoryToWhatsApp}
                          disabled={!isWhatsAppEnabled}
                          className={`${isWhatsAppEnabled ? 'text-green-700 border-green-300 hover:bg-green-50 hover:border-green-400' : 'opacity-60'}`}
                          data-testid="copy-inventory-whatsapp-btn"
                        >
                          <Copy className="w-4 h-4 mr-2" />
                          Copiar WhatsApp
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {!isWhatsAppEnabled && (
                      <TooltipContent>
                        <p>Selecciona una bodega para copiar su inventario</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
                {justoOrigenIds.includes(warehouseFilter) && (
                  <Button variant="outline" onClick={() => setStockJustoOpen(true)} className="text-orange-700 border-orange-300 hover:bg-orange-50" data-testid="export-stock-justo-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Exportar recuento para Justo
                  </Button>
                )}
                <Button variant="outline" onClick={exportInventoryCSV}>
                  <Download className="w-4 h-4 mr-2" />
                  Exportar CSV
                </Button>
              </div>
            </div>
          </div>

          {/* Resumen de inventario */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Productos</p>
              <p className="text-2xl font-bold text-slate-900">{inventoryStats.totalProducts}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Stock Total</p>
              <p className="text-2xl font-bold text-primary">{inventoryStats.totalStock}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Stock Bajo</p>
              <p className="text-2xl font-bold text-amber-600">{inventoryStats.lowStock}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-sm text-slate-500">Sin Stock</p>
              <p className="text-2xl font-bold text-red-600">{inventoryStats.outOfStock}</p>
            </div>
          </div>

          {/* Tabla de inventario actual */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {/* Header con botón de vista en cajas */}
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <span className="text-sm font-medium text-slate-700">
                {filteredInventory.length} productos
              </span>
              <Button
                variant={showInBoxes ? "default" : "outline"}
                size="sm"
                onClick={() => setShowInBoxes(!showInBoxes)}
                data-testid="toggle-boxes-view"
              >
                <Boxes className="w-4 h-4 mr-2" />
                {showInBoxes ? 'Ver en Unidades' : 'Ver en Cajas'}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Producto
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Bodega
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Proveedor
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      {showInBoxes ? 'Stock (Cajas)' : 'Stock'}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Stock Mín.
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredInventory.map((item) => {
                    const stock = item.display_stock ?? item.current_stock ?? 0;
                    const minStock = item.minimum_stock || 0;
                    const isLow = stock <= minStock && stock > 0;
                    const isOut = stock === 0;
                    const warehouse = warehouses.find(w => w.id === item.warehouse_id);
                    
                    // Calcular stock en cajas
                    const unidadesPorCaja = item.unidades_por_caja;
                    const stockEnCajas = unidadesPorCaja && unidadesPorCaja > 0 
                      ? (stock / unidadesPorCaja).toFixed(2) 
                      : null;
                    
                    return (
                      <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-mono text-slate-600">{item.code}</span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium text-slate-900">{item.name}</p>
                          {item.maestros && item.maestros.length > 0 && (
                            <p className="text-xs text-slate-400">{item.maestros.join(', ')}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                            warehouse?.code === 'BODEGA-MALL' 
                              ? 'bg-purple-100 text-purple-700' 
                              : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {warehouse?.name || 'Sin asignar'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-700">{item.supplier || '-'}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {showInBoxes ? (
                            stockEnCajas !== null ? (
                              <div>
                                <span className={`text-sm font-bold ${
                                  isOut ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-slate-900'
                                }`}>
                                  {stockEnCajas}
                                </span>
                                <span className="text-xs text-slate-400 ml-1">cajas</span>
                                <p className="text-xs text-slate-400">({stock} {item.unit || 'und'})</p>
                              </div>
                            ) : (
                              <div>
                                <span className={`text-sm font-bold ${
                                  isOut ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-slate-900'
                                }`}>
                                  {stock}
                                </span>
                                <span className="text-xs text-slate-400 ml-1">{item.unit || 'und'}</span>
                                <p className="text-xs text-amber-500">Sin config. cajas</p>
                              </div>
                            )
                          ) : (
                            <>
                              <span className={`text-sm font-bold ${
                                isOut ? 'text-red-600' : isLow ? 'text-amber-600' : 'text-slate-900'
                              }`}>
                                {stock}
                              </span>
                              <span className="text-xs text-slate-400 ml-1">{item.unit || 'und'}</span>
                            </>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm text-slate-500">
                          {minStock}
                        </td>
                        <td className="px-4 py-3">
                          {isOut ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              Sin stock
                            </span>
                          ) : isLow ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                              Stock bajo
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              Normal
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            {filteredInventory.length === 0 && (
              <div className="text-center py-12">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No hay productos que coincidan con los filtros</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Tab: Historial de Movimientos */}
        <TabsContent value="history" className="space-y-6">
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Filtrar por producto</label>
            <Select value={selectedItem} onValueChange={setSelectedItem}>
              <SelectTrigger>
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Todos los productos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los productos</SelectItem>
                {items.map(item => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.code} - {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-40">
            <label className="text-sm font-medium text-slate-700 mb-1 block">Mostrar</label>
            <Select value={limit.toString()} onValueChange={(v) => setLimit(parseInt(v))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">Últimos 25</SelectItem>
                <SelectItem value="50">Últimos 50</SelectItem>
                <SelectItem value="100">Últimos 100</SelectItem>
                <SelectItem value="200">Últimos 200</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Total movimientos</p>
          <p className="text-2xl font-bold text-slate-900">{movements.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Entradas</p>
          <p className="text-2xl font-bold text-green-600">
            {movements.filter(m => m.movement_type === 'add').length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Salidas</p>
          <p className="text-2xl font-bold text-red-600">
            {movements.filter(m => m.movement_type === 'subtract').length}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm text-slate-500">Ajustes</p>
          <p className="text-2xl font-bold text-blue-600">
            {movements.filter(m => m.movement_type === 'set').length}
          </p>
        </div>
      </div>

      {/* Lista de movimientos */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Fecha y Hora
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Producto
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Cantidad
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Stock Anterior
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Stock Nuevo
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Motivo
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                  Usuario
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {movements.map((movement, index) => {
                const typeInfo = getMovementTypeInfo(movement.movement_type);
                const TypeIcon = typeInfo.icon;
                
                return (
                  <tr key={movement.id || index} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        <span className="text-sm font-medium text-slate-900">
                          {formatDate(movement.created_at)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{movement.item_name}</p>
                        <p className="text-xs text-slate-500">{movement.item_code}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
                        <TypeIcon className="w-3 h-3" />
                        {typeInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-bold ${
                        movement.movement_type === 'add' || movement.movement_type === 'reception' ? 'text-green-600' :
                        movement.movement_type === 'subtract' ? 'text-red-600' : 'text-blue-600'
                      }`}>
                        {movement.movement_type === 'add' || movement.movement_type === 'reception' ? '+' : 
                         movement.movement_type === 'subtract' ? '-' : ''}
                        {movement.quantity || movement.quantity_change || 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-slate-500">
                      {movement.previous_stock}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-slate-900">
                      {movement.new_stock}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getReasonBadgeColor(movement.reason || movement.reference_type || 'otro')}`}>
                        {movement.reason || (movement.reference_type === 'reception' ? `Recepción - ${movement.reference_number || 'Factura'}` : 'Sin especificar')}
                      </span>
                      {movement.notes && (
                        <p className="text-xs text-slate-400 mt-1 max-w-[150px] truncate" title={movement.notes}>
                          {movement.notes}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-400" />
                        <span className="text-sm text-slate-700">{movement.created_by_name || 'Sistema'}</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {movements.length === 0 && (
          <div className="text-center py-12">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No hay movimientos de inventario registrados</p>
          </div>
        )}
      </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminInventoryPage;
